/* eslint-disable @typescript-eslint/no-explicit-any */
import { ENDPOINTS } from '@/util'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useGetFilterSelections } from '@/catalog-apps/hooks/useGetFilterSelections'
import { ChartBarResponse } from './types'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useGetPageFilters } from '../../AppPage2/Filter/useGetPageFilters'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'
import { TComponentType } from '../../AppPage2/types'

const USE_MOCK_DATA = false

interface GetChartBarParams {
  appKey?: string
  contentId?: string
  projectId?: number
  filterSelections: Record<string, any>[]
  isPageBuilder?: boolean
}

const getChartBar = async ({ appKey, contentId, projectId, filterSelections, isPageBuilder }: GetChartBarParams) => {
  if (isPageBuilder) {
    return Promise.resolve({
      ...(await import('@/catalog-apps/mock-data/chart-bar/chart-bar-response-v2.json')).json,
    } as ChartBarResponse)
  }
  if (!appKey || !contentId || !projectId) {
    throw new Error('Missing required parameters')
  }
  if (isPageBuilder) {
    return Promise.resolve({
      ...(await import('@/catalog-apps/mock-data/chart-bar/chart-bar-response-v2.json')).json,
    } as ChartBarResponse)
  }

  if (USE_MOCK_DATA) {
    return (await import('@/catalog-apps/mock-data/chart-bar/chart-bar-response-v2.json')).json as ChartBarResponse
  }

  const token = JSON.parse(sessionStorage.getItem('token') || '')
  if (!token) {
    throw new Error('No token found')
  }

  const url = `${ENDPOINTS.CC_DOTNET}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      app_id: appKey,
      content_id: contentId,
      project_id: projectId,
      filters: filterSelections,
    }),
  })

  const data = (await response.json()) as ChartBarResponse
  if (response.ok) {
    return data
  }
  throw new Error('Failed to fetch chartBar data')
}

export function useGetChartBar(contentId: string, tileFilterSelections: Record<string, any>[]) {
  const isPageBuilder = useIsPageBuilder()
  const {
    data: { defaultSelections },
  } = useGetPageFilters()
  const { filterSelections } = useGetFilterSelections()
  const { appKey, projectId } = useAppContext()
  const filterSelectionsArray =
    filterSelections.length > 0
      ? [...filterSelections, ...tileFilterSelections]
      : [...defaultSelections, ...tileFilterSelections]

  const componentType: TComponentType = 'chart-bar'
  return useSuspenseQuery({
    queryKey: [contentId, componentType, appKey, projectId, filterSelectionsArray, isPageBuilder],
    queryFn: () =>
      getChartBar({
        appKey,
        contentId,
        projectId,
        filterSelections: filterSelectionsArray,
        isPageBuilder,
      }),
  })
}
